import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

interface ImportResult {
  success_count: number;
  error_count: number;
  skipped_count: number;
  total_count: number;
  errors: Array<{
    row: number;
    service_id?: string;
    error: string;
  }>;
}

const ImportPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string>('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError('');
        setResult(null);
      } else {
        setError('Please select a CSV file');
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.importCustomersCSV(file);
      setResult(response);
      
      // Clear the file input after successful import
      const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      setFile(null);
    } catch (error: any) {
      console.error('Import failed:', error);
      setError(error.response?.data?.detail || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (hasErrors: boolean, hasWarnings: boolean) => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getStatusIcon = (hasErrors: boolean, hasWarnings: boolean) => {
    if (hasErrors) return <ErrorIcon />;
    if (hasWarnings) return <WarningIcon />;
    return <SuccessIcon />;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Import Customers
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Upload a CSV file to import customer data into the system
      </Typography>

      {/* Upload Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            CSV File Upload
          </Typography>
          
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Choose CSV File
                </Button>
              </label>
              {file && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={!file || loading}
                fullWidth
                size="large"
              >
                {loading ? 'Importing...' : 'Import Customers'}
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Skip duplicate service IDs"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Update existing customers"
              />
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processing CSV file...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* CSV Format Guide */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            CSV Format Requirements
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Your CSV file should include the following columns (service_id is required):
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Chip label="service_id*" color="primary" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="member_id" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="organisation_name" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="contact_first_name" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="contact_last_name" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="mobile_number" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="email" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="service_type" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="service_address" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
            <Chip label="status" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            * Required field. Service types: nbn, mobile, voice, data. Status: active, inactive, suspended.
          </Typography>
        </CardContent>
      </Card>

      {/* Import Results */}
      {result && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              {getStatusIcon(result.error_count > 0, result.skipped_count > 0)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                Import Results
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="h4">{result.success_count}</Typography>
                  <Typography variant="body2">Imported</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                  <Typography variant="h4">{result.error_count}</Typography>
                  <Typography variant="body2">Errors</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                  <Typography variant="h4">{result.skipped_count}</Typography>
                  <Typography variant="body2">Skipped</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                  <Typography variant="h4">{result.total_count}</Typography>
                  <Typography variant="body2">Total</Typography>
                </Paper>
              </Grid>
            </Grid>

            {result.errors && result.errors.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom color="error">
                  Import Errors
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Service ID</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.errors.map((error, index) => (
                        <TableRow key={index}>
                          <TableCell>{error.row}</TableCell>
                          <TableCell>{error.service_id || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {error.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {result.success_count > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Successfully imported {result.success_count} customer{result.success_count !== 1 ? 's' : ''}.
                {result.error_count === 0 && result.skipped_count === 0 && ' All records processed successfully!'}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ImportPage;
